import { FC, Fragment, useState } from 'react';
import dayjs from 'dayjs';
import {
	ArrowLeftOutlined,
	EditOutlined,
	EyeOutlined,
	LoadingOutlined,
	UploadOutlined,
} from '@ant-design/icons';
import {
	Alert,
	Button,
	Col,
	DatePicker,
	Divider,
	Form,
	Input,
	Row,
	Select,
	Space,
	Table,
	Typography,
	Upload,
	UploadFile,
} from 'antd';
import { useApp } from '@/App';
import { useModals } from '@/components/modals/ModalProvider';
import FleetModal, { useAsyncModal } from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import { useForm } from '@/libs/antd/form';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import {
	useMonitoringCovenantSPFDetail,
	useMonitoringUpdateCovenant,
} from '@/libs/fleet-api/services/monitoring';
import { FileData } from '@/libs/fleet-api/services/types';
import { uploader } from '@/libs/fleet-api/uploader';
import { useMutation } from '@tanstack/react-query';
import rule from '@/libs/antd/rules';
import { useUserData } from '@/libs/fleet-api/hooks';

import { NAPNJFModal, useNAPNJFModal } from '@/pages/nap/NAPDownloadPage';
import { NAPJFModal, useNAPJFModal } from '@/pages/nap/NAPJFDownloadPage';

export type CovenantEditData = {
	id: number;
	covenant: string;
	category: { id: number; value: string };
	priority: { id: number; value: string };
	repeatFrequency: { id: number; value: string };
	monitoringType: { id: number; value: string };
	furtherAnalysis: { id: number; value: string };
	uploadDocumentDate: string | null;
	notes: string;
	fcohNotes: string;
	covenantDocument?: {
		id: string;
		name: string;
		path: string;
		url: string;
		format: string;
	};
	status: string;
	dueDate?: string;
	overdueDays?: number;
};

export const useEditCovenantFRMModal = () =>
	useAsyncModal<
		{ covenantData: CovenantEditData; customerId?: string },
		CovenantEditData
	>();

export const useEditCovenantFCOHModal = () =>
	useAsyncModal<{ covenantData: CovenantEditData }, CovenantEditData>();

type EditCovenantFRMModalProps = ReturnType<typeof useEditCovenantFRMModal>;
type EditCovenantFCOHModalProps = ReturnType<typeof useEditCovenantFCOHModal>;

const EditCovenantFRMModal: FC<EditCovenantFRMModalProps> = ({
	data,
	modal,
	close,
}) => {
	const customerId = data?.customerId;

	const { form, fieldName } = useForm<{
		uploadDocumentDate: string | dayjs.Dayjs;
		covenantDocument: FileData | null;
		notes: string;
	}>();

	const uploadMutation = useMutation({
		mutationFn: uploader,
	});

	const updateMutation = useMonitoringUpdateCovenant();

	const handleSubmit = async (values: {
		uploadDocumentDate: string | dayjs.Dayjs;
		covenantDocument: FileData | null;
		notes: string;
	}) => {
		try {
			// Validate form fields first
			await form.validateFields();

			// Convert FileData to FilePayload format expected by API
			let covenantDocument = undefined;
			if (values.covenantDocument?.id) {
				covenantDocument = {
					id: values.covenantDocument.id,
					name: values.covenantDocument.name,
					path: values.covenantDocument.path,
				};
			}

			// Convert dayjs object to ISO string format for API
			let uploadDocumentDate = '';
			if (values.uploadDocumentDate) {
				if (dayjs.isDayjs(values.uploadDocumentDate)) {
					// Convert dayjs to ISO string (YYYY-MM-DDTHH:mm:ss.sssZ)
					uploadDocumentDate = values.uploadDocumentDate.toISOString();
				} else if (typeof values.uploadDocumentDate === 'string') {
					// Check if it's DD/MM/YYYY format and convert to ISO
					const ddmmyyyyRegex = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
					const match = values.uploadDocumentDate.match(ddmmyyyyRegex);
					if (match) {
						const [, day, month, year] = match;
						const date = dayjs(
							`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`,
						);
						if (date.isValid()) {
							uploadDocumentDate = date.toISOString();
						} else {
							uploadDocumentDate = values.uploadDocumentDate;
						}
					} else {
						// Try parsing as other date formats
						const parsedDate = dayjs(values.uploadDocumentDate);
						if (parsedDate.isValid()) {
							uploadDocumentDate = parsedDate.toISOString();
						} else {
							uploadDocumentDate = values.uploadDocumentDate;
						}
					}
				} else {
					// Handle other date formats by converting to ISO string
					uploadDocumentDate = new Date(
						values.uploadDocumentDate,
					).toISOString();
				}
			}

			await updateMutation.mutateAsync({
				covenantId: data!.covenantData.id,
				covenantDocument: covenantDocument!,
				uploadDocumentDate,
				notes: values.notes,
				status: data!.covenantData.status,
			});

			// Refresh data after successful update
			window.location.reload();
		} catch (error) {
			console.error('Failed to update covenant:', error);
		}
	};

	return (
		<FleetModal
			{...modal}
			width={800}
			closable={false}
			buttonPosition="end"
			okText="SAVE"
			cancelText="BACK"
			onCancel={() => close()}
			onOk={form.submit}
			okButtonProps={{
				disabled: updateMutation.isPending,
				loading: updateMutation.isPending,
			}}
			cancelButtonProps={{
				disabled: updateMutation.isPending,
			}}
			afterOpenChange={(isOpen) => {
				if (isOpen && data?.covenantData) {
					// Format date for DatePicker (DD/MM/YYYY format)
					let formattedDate = null;
					if (data.covenantData.uploadDocumentDate) {
						const date = new Date(data.covenantData.uploadDocumentDate);
						const day = String(date.getDate()).padStart(2, '0');
						const month = String(date.getMonth() + 1).padStart(2, '0');
						const year = date.getFullYear();
						formattedDate = `${day}/${month}/${year}`;
					}

					// Set existing covenant document if available
					let covenantDocument = null;
					if (data.covenantData.covenantDocument?.id) {
						covenantDocument = {
							id: data.covenantData.covenantDocument.id,
							name: data.covenantData.covenantDocument.name,
							path: data.covenantData.covenantDocument.path,
							url: data.covenantData.covenantDocument.url,
							format: data.covenantData.covenantDocument.format,
						} as FileData;
					}

					form.setFieldsValue({
						uploadDocumentDate: formattedDate || undefined,
						covenantDocument,
						notes: data.covenantData.notes || '',
					});
				} else if (!isOpen) {
					form.resetFields();
				}
			}}
		>
			<div style={{ marginBottom: 20 }}>
				<FleetTitle>Edit Data Covenant</FleetTitle>
			</div>

			<Form
				form={form}
				{...ANTD_DEFAULT_PROPS.FORM}
				onFinish={handleSubmit}
			>
				{/* Covenant - Read Only */}
				<Form.Item
					label="Covenant"
					labelCol={{ span: 6 }}
					wrapperCol={{ span: 18 }}
				>
					<Input.TextArea
						rows={3}
						value={data?.covenantData.covenant || ''}
						readOnly
						style={{ backgroundColor: '#f5f5f5', color: '#666' }}
					/>
				</Form.Item>

				<Divider />

				{/* Kategori dan Priority - Read Only */}
				<Row gutter={40}>
					<Col span={12}>
						<Form.Item label="Kategori">
							<Input
								value={data?.covenantData.category?.value || '-'}
								readOnly
								style={{ backgroundColor: '#f5f5f5', color: '#666' }}
							/>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item label="Priority">
							<Input
								value={data?.covenantData.priority?.value || '-'}
								readOnly
								style={{ backgroundColor: '#f5f5f5', color: '#666' }}
							/>
						</Form.Item>
					</Col>
				</Row>

				<Divider />

				{/* Repeat dan Jenis Monitoring - Read Only */}
				<Row gutter={40}>
					<Col span={12}>
						<Form.Item label="Repeat">
							<Input
								value={data?.covenantData.repeatFrequency?.value || '-'}
								readOnly
								style={{ backgroundColor: '#f5f5f5', color: '#666' }}
							/>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item label="Jenis Monitoring">
							<Input
								value={data?.covenantData.monitoringType?.value || '-'}
								readOnly
								style={{ backgroundColor: '#f5f5f5', color: '#666' }}
							/>
						</Form.Item>
					</Col>
				</Row>

				<Divider />

				<Row gutter={40}>
					<Col span={12}>
						{/* Analisa Lebih Lanjut - Read Only */}
						<Form.Item label="Analisa Lebih Lanjut">
							<Input
								value={data?.covenantData.furtherAnalysis?.value || '-'}
								readOnly
								style={{ backgroundColor: '#f5f5f5', color: '#666' }}
							/>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							name={fieldName('uploadDocumentDate')}
							label="Tanggal Upload Dokumen"
							labelCol={{ span: 12 }}
							wrapperCol={{ span: 12 }}
							getValueProps={(value) => {
								if (!value) return { value: undefined };

								try {
									// Handle various date formats
									let parsedDate;
									if (typeof value === 'string') {
										// Try parsing as ISO string first
										parsedDate = dayjs(value);
										// If not valid, try other formats
										if (!parsedDate.isValid()) {
											parsedDate = dayjs(value, 'YYYY-MM-DD');
										}
										if (!parsedDate.isValid()) {
											parsedDate = dayjs(value, 'DD/MM/YYYY');
										}
									} else if (value instanceof Date) {
										parsedDate = dayjs(value);
									} else {
										return { value: undefined };
									}

									return {
										value: parsedDate.isValid()
											? parsedDate.local()
											: undefined,
									};
								} catch {
									return { value: undefined };
								}
							}}
							normalize={(value) =>
								value && dayjs(value).utc(true).toISOString()
							}
							rules={[rule.required()]}
						>
							<DatePicker
								style={{ width: '100%' }}
								format="DD/MM/YYYY"
								placeholder="Pilih tanggal upload"
							/>
						</Form.Item>
					</Col>
				</Row>

				<Divider />

				{/* Upload Dokumen dan Tanggal Upload - Editable */}
				<Row gutter={40}>
					<Col span={12}>
						<Form.Item
							name={fieldName('covenantDocument')}
							label="Upload Dokumen"
							labelCol={{ span: 12 }}
							wrapperCol={{ span: 12 }}
							rules={[rule.required()]}
							getValueProps={(value) => {
								const file = value as FileData;
								return {
									fileList: file?.id
										? ([
												{
													uid: file.id,
													name: file.name,
													thumbUrl: file.url,
												},
											] as UploadFile[])
										: [],
								};
							}}
							normalize={({ file }: { file: UploadFile }) => {
								return {
									id: file.uid,
									format: file.type,
									name: file.name,
									url: file.thumbUrl,
								} as FileData;
							}}
						>
							<Upload.Dragger
								listType="picture"
								accept=".pdf,.jpg,.jpeg,.png"
								maxCount={1}
								beforeUpload={() => false}
								showUploadList={true}
								onRemove={() => {
									form.setFieldValue(fieldName('covenantDocument'), null);
									return false;
								}}
								onChange={async ({ file }) => {
									const fileid = Date.now().toString();
									const filepath = `customer/${customerId || 'unknown'}/monitoring/covenant`;

									try {
										const url = await uploadMutation.mutateAsync({
											file: file as unknown as File,
											fileid,
											filepath,
										});

										form.setFieldValue(fieldName('covenantDocument'), {
											format: 'pdf',
											id: fileid,
											name: file.name,
											path: filepath,
											url,
										});
									} catch {
										form.setFieldValue(fieldName('covenantDocument'), null);
									}
								}}
							>
								<UploadOutlined
									style={{
										fontSize: 25,
										color: '#BABABA',
										display: 'block',
									}}
								/>
							</Upload.Dragger>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item label="Due Date">
							{data?.covenantData.dueDate
								? dayjs(data.covenantData.dueDate).format('DD/MM/YYYY')
								: '-'}
						</Form.Item>
					</Col>
				</Row>

				<Divider />

				{/* Keterangan - Editable */}
				<Form.Item
					label="Keterangan"
					name={fieldName('notes')}
					labelCol={{ span: 6 }}
					wrapperCol={{ span: 18 }}
				>
					<Input.TextArea
						rows={3}
						placeholder="Masukkan keterangan"
					/>
				</Form.Item>
			</Form>
		</FleetModal>
	);
};

const EditCovenantFCOHModal: FC<EditCovenantFCOHModalProps> = ({
	data,
	modal,
	close,
}) => {
	const { form, fieldName } = useForm<{
		status: string;
		fcohNotes: string;
	}>();

	const updateMutation = useMonitoringUpdateCovenant();

	const handleSubmit = async (values: {
		status: string;
		fcohNotes: string;
	}) => {
		try {
			// Validate form fields first
			await form.validateFields();

			// Convert FileData to FilePayload format expected by API
			let covenantDocument;
			if (data!.covenantData.covenantDocument?.id) {
				covenantDocument = {
					id: data!.covenantData.covenantDocument.id,
					name: data!.covenantData.covenantDocument.name,
					path: data!.covenantData.covenantDocument.path,
				};
			} else {
				// Create a dummy document if none exists (API requires it)
				covenantDocument = {
					id: '',
					name: '',
					path: '',
				};
			}

			await updateMutation.mutateAsync({
				covenantId: data!.covenantData.id,
				covenantDocument,
				uploadDocumentDate: data!.covenantData.uploadDocumentDate || '',
				notes: values.fcohNotes,
				status: values.status,
			});

			// Refresh data after successful update
			window.location.reload();
		} catch (error) {
			console.error('Failed to update covenant:', error);
		}
	};

	return (
		<FleetModal
			{...modal}
			width={800}
			closable={false}
			buttonPosition="end"
			okText="SAVE"
			cancelText="BACK"
			onCancel={() => close()}
			onOk={form.submit}
			okButtonProps={{
				disabled: updateMutation.isPending,
				loading: updateMutation.isPending,
			}}
			cancelButtonProps={{
				disabled: updateMutation.isPending,
			}}
			afterOpenChange={(isOpen) => {
				if (isOpen && data?.covenantData) {
					form.setFieldsValue({
						status: data.covenantData.status || 'pending',
						fcohNotes: data.covenantData.fcohNotes || '',
					});
				} else if (!isOpen) {
					form.resetFields();
				}
			}}
		>
			<div style={{ marginBottom: 20 }}>
				<FleetTitle>Edit Data Covenant</FleetTitle>
			</div>

			<Form
				form={form}
				{...ANTD_DEFAULT_PROPS.FORM}
				onFinish={handleSubmit}
			>
				{/* Covenant - Read Only */}
				<Form.Item
					label="Covenant"
					labelCol={{ span: 6 }}
					wrapperCol={{ span: 18 }}
				>
					<Input.TextArea
						rows={3}
						value={data?.covenantData.covenant || ''}
						readOnly
						style={{ backgroundColor: '#f5f5f5', color: '#666' }}
					/>
				</Form.Item>

				<Divider />

				{/* Kategori dan Priority - Read Only */}
				<Row gutter={40}>
					<Col span={12}>
						<Form.Item label="Kategori">
							<Input
								value={data?.covenantData.category?.value || '-'}
								readOnly
								style={{ backgroundColor: '#f5f5f5', color: '#666' }}
							/>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item label="Priority">
							<Input
								value={data?.covenantData.priority?.value || '-'}
								readOnly
								style={{ backgroundColor: '#f5f5f5', color: '#666' }}
							/>
						</Form.Item>
					</Col>
				</Row>

				<Divider />

				{/* Repeat dan Jenis Monitoring - Read Only */}
				<Row gutter={40}>
					<Col span={12}>
						<Form.Item label="Repeat">
							<Input
								value={data?.covenantData.repeatFrequency?.value || '-'}
								readOnly
								style={{ backgroundColor: '#f5f5f5', color: '#666' }}
							/>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item label="Jenis Monitoring">
							<Input
								value={data?.covenantData.monitoringType?.value || '-'}
								readOnly
								style={{ backgroundColor: '#f5f5f5', color: '#666' }}
							/>
						</Form.Item>
					</Col>
				</Row>

				<Divider />

				<Row gutter={40}>
					<Col span={12}>
						{/* Analisa Lebih Lanjut - Read Only */}
						<Form.Item label="Analisa Lebih Lanjut">
							<Input
								value={data?.covenantData.furtherAnalysis?.value || '-'}
								readOnly
								style={{ backgroundColor: '#f5f5f5', color: '#666' }}
							/>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							name={fieldName('status')}
							label="Status"
							labelCol={{ span: 12 }}
							wrapperCol={{ span: 12 }}
							rules={[rule.required()]}
						>
							<Select
								style={{ width: '100%' }}
								placeholder="Pilih status"
								options={[
									{ label: 'Done', value: 'done' },
									{ label: 'Pending', value: 'pending' },
								]}
							/>
						</Form.Item>
					</Col>
				</Row>

				<Divider />

				{/* Tanggal Upload Dokumen - Read Only */}
				<Row gutter={40}>
					<Col span={12}>
						<Form.Item label="Tanggal Upload Dokumen">
							<Input
								value={
									data?.covenantData.uploadDocumentDate
										? (() => {
												try {
													return new Date(
														data.covenantData.uploadDocumentDate!,
													).toLocaleDateString('id-ID', {
														day: '2-digit',
														month: '2-digit',
														year: 'numeric',
													});
												} catch {
													return '-';
												}
											})()
										: '-'
								}
								readOnly
								style={{ backgroundColor: '#f5f5f5', color: '#666' }}
							/>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item label="Due Date">
							<Input
								value={
									data?.covenantData.dueDate
										? (() => {
												try {
													return new Date(
														data.covenantData.dueDate,
													).toLocaleDateString('id-ID', {
														day: '2-digit',
														month: '2-digit',
														year: 'numeric',
													});
												} catch {
													return '-';
												}
											})()
										: '-'
								}
								readOnly
								style={{ backgroundColor: '#f5f5f5', color: '#666' }}
							/>
						</Form.Item>
					</Col>
				</Row>

				<Divider />

				{/* OD - Read Only */}
				<Form.Item label="OD">
					<Input
						value={`${data?.covenantData.overdueDays ?? 0} Hari`}
						readOnly
						style={{ backgroundColor: '#f5f5f5', color: '#666' }}
					/>
				</Form.Item>

				<Divider />

				{/* Keterangan FRM - Read Only */}
				<Form.Item label="Keterangan FRM">
					<Input.TextArea
						rows={3}
						value={data?.covenantData.notes || '-'}
						readOnly
						style={{ backgroundColor: '#f5f5f5', color: '#666' }}
					/>
				</Form.Item>

				<Divider />

				{/* Keterangan FCOH - Editable */}
				<Form.Item
					label="Keterangan FCOH"
					name={fieldName('fcohNotes')}
					labelCol={{ span: 6 }}
					wrapperCol={{ span: 18 }}
				>
					<Input.TextArea
						rows={3}
						placeholder="Masukkan keterangan FCOH"
					/>
				</Form.Item>
			</Form>
		</FleetModal>
	);
};

const MonitoringCovenantPage: FC<{
	resolutionId: number;
	spfId?: number;
}> = ({ resolutionId, spfId }) => {
	const { navigate } = useApp();

	const [form] = Form.useForm();
	const [searchBy, setSearchBy] = useState<string>('');
	const [searchValue, setSearchValue] = useState<string>('');
	const [dateRange, setDateRange] = useState<[string, string] | null>(null);

	const { data, isLoading, error } = useMonitoringCovenantSPFDetail(
		resolutionId,
		spfId,
	);

	const { openPreviewModal, openCustomerGroupDetail, openCustomerDetail } =
		useModals();
	const userData = useUserData();
	const editCovenantFRMModal = useEditCovenantFRMModal();
	const editCovenantFCOHModal = useEditCovenantFCOHModal();
	const napNJFModal = useNAPNJFModal();
	const napJFModal = useNAPJFModal();

	const handleSearchByChange = (value: string) => {
		setSearchBy(value);
		setSearchValue('');
		form.resetFields(['searchValue']);
	};

	const handleSearch = () => {
		const values = form.getFieldsValue();
		if (values.searchBy && values.searchValue) {
			setSearchBy(values.searchBy);
			setSearchValue(values.searchValue);
		}
	};

	const formatDate = (dateString: string) => {
		return new Date(dateString).toLocaleDateString('id-ID', {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric',
		});
	};

	if (isLoading) {
		return (
			<div
				style={{
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					height: '200px',
				}}
			>
				<LoadingOutlined
					style={{ fontSize: 24 }}
					spin
				/>
			</div>
		);
	}

	if (error) {
		return (
			<div style={{ color: 'red', textAlign: 'center', padding: '20px' }}>
				Gagal memuat data covenant.
			</div>
		);
	}

	if (!data) {
		return (
			<div style={{ textAlign: 'center', padding: '20px' }}>
				Data tidak ditemukan.
			</div>
		);
	}

	// Filter covenants based on single search criteria
	const filteredCovenants =
		data.covenants?.filter((covenant) => {
			if (!searchBy) return true; // No filter applied

			switch (searchBy) {
				case 'category':
					return covenant.category?.value === searchValue;
				case 'priority':
					return (
						covenant.priority?.value?.toLowerCase() ===
						searchValue?.toLowerCase()
					);
				case 'status':
					return covenant.status?.toLowerCase() === searchValue?.toLowerCase();
				case 'uploadDate':
					if (covenant.uploadDocumentDate && dateRange) {
						const [startDateStr, endDateStr] = dateRange;

						// Parse DD/MM/YYYY format to Date object
						const parseDateString = (dateStr: string) => {
							const [day, month, year] = dateStr.split('/');
							return new Date(
								parseInt(year),
								parseInt(month) - 1,
								parseInt(day),
							);
						};

						const uploadDate = new Date(covenant.uploadDocumentDate);
						const startDate = parseDateString(startDateStr);
						const endDate = parseDateString(endDateStr);

						// Check if upload date is within range (inclusive)
						return uploadDate >= startDate && uploadDate <= endDate;
					}
					return false;
				default:
					return true;
			}
		}) || [];

	return (
		<Fragment>
			<NAPNJFModal {...napNJFModal} />
			<NAPJFModal {...napJFModal} />
			{/* Header */}
			<div
				style={{
					display: 'flex',
					gap: 10,
					marginBottom: 40,
				}}
			>
				<button
					style={{
						display: 'flex',
						height: 44,
						width: 44,
						fontSize: 24,
						border: 'none',
						alignItems: 'center',
						justifyContent: 'center',
						background: '#1557BC',
						color: 'white',
						cursor: 'pointer',
					}}
					onClick={() =>
						navigate('MonitoringCovenantCustomerPage', {
							customerId: data.customerId,
						})
					}
				>
					<ArrowLeftOutlined />
				</button>
				<FleetTitle style={{ margin: 0, flex: 1 }}>Detail Covenant</FleetTitle>
			</div>

			{userData?.groupName === 'SECOM' ? (
				<Fragment>
					<Row
						gutter={20}
						style={{ marginBottom: 30 }}
					>
						<Col span={12}>
							{/* Nama Customer */}
							<Row style={{ marginBottom: 20 }}>
								<Col span={12}>
									<Typography.Text strong>Nama Customer</Typography.Text>
								</Col>
								<Col span={12}>
									<Typography.Text>
										<a
											href="#"
											className="trigger-popup"
											onClick={(e) => {
												e.preventDefault();
												openCustomerDetail(data.customerId);
											}}
										>
											{data.customerName ?? '-'}
										</a>
									</Typography.Text>
								</Col>
							</Row>
						</Col>

						<Col span={12}>
							{/* Group Customer BMPP */}
							<Row style={{ marginBottom: 20 }}>
								<Col span={12}>
									<Typography.Text strong>Group Customer BMPP</Typography.Text>
								</Col>
								<Col span={12}>
									<Typography.Text>
										{data.bmppGroupId ? (
											<a
												href="#"
												className="trigger-popup"
												onClick={(e) => {
													e.preventDefault();
													openCustomerGroupDetail(data.bmppGroupId);
												}}
											>
												{data.bmppGroupName ?? '-'}
											</a>
										) : (
											<>{data.bmppGroupName ?? '-'}</>
										)}
									</Typography.Text>
								</Col>
							</Row>
						</Col>
						<Col span={12}>
							{/* NAP ID (napNo) */}
							<Row style={{ marginBottom: 20 }}>
								<Col span={12}>
									<Typography.Text strong>NAP ID</Typography.Text>
								</Col>
								<Col span={12}>
									<Typography.Text>{data.napNo ?? '-'}</Typography.Text>
								</Col>
							</Row>
						</Col>
						<Col span={12}>
							{/* Summary NAP */}
							<Row style={{ marginBottom: 20 }}>
								<Col span={12}>
									<Typography.Text strong>Summary NAP</Typography.Text>
								</Col>
								<Col span={12}>
									<a
										href="#"
										onClick={(e) => {
											e.preventDefault();
											if (data.plafonType === 'Join Financing') {
												napJFModal.open({ prospectId: data.prospectId });
											} else {
												napNJFModal.open({ prospectId: data.prospectId });
											}
										}}
									>
										View Dokumen
									</a>
								</Col>
							</Row>
						</Col>
						<Col span={12}>
							{/* No.MKL */}
							<Row style={{ marginBottom: 20 }}>
								<Col span={12}>
									<Typography.Text strong>No.MKL</Typography.Text>
								</Col>
								<Col span={12}>
									<Typography.Text>{data.mklNo ?? '-'}</Typography.Text>
								</Col>
							</Row>
						</Col>
						<Col span={12}>
							{/* Summary Risalah */}
							<Row style={{ marginBottom: 20 }}>
								<Col span={12}>
									<Typography.Text strong>Summary Risalah</Typography.Text>
								</Col>
								<Col span={12}>
									{data.resolutionFile ? (
										<Button
											type="link"
											style={{ padding: 0, height: 'auto' }}
											onClick={() => {
												console.log('Open Summary Risalah Modal');
												if (data.resolutionFile) {
													openPreviewModal(data.resolutionFile);
												}
											}}
										>
											View Dokumen
										</Button>
									) : (
										<Typography.Text>-</Typography.Text>
									)}
								</Col>
							</Row>
						</Col>
						<Col span={12}>
							{/* Sumber Order */}
							<Row style={{ marginBottom: 20 }}>
								<Col span={12}>
									<Typography.Text strong>Sumber Order</Typography.Text>
								</Col>
								<Col span={12}>
									<Typography.Text>{data.orderSource ?? '-'}</Typography.Text>
								</Col>
							</Row>
						</Col>
						<Col span={12}>
							{/* Ultimate Owner */}
							<Row style={{ marginBottom: 20 }}>
								<Col span={12}>
									<Typography.Text strong>Ultimate Owner</Typography.Text>
								</Col>
								<Col span={12}>
									<Typography.Text>{data.ultimateOwner ?? '-'}</Typography.Text>
								</Col>
							</Row>
						</Col>
					</Row>
				</Fragment>
			) : (
				<Fragment>
					{/* Customer & SPF Info */}
					<Row
						gutter={20}
						style={{ marginBottom: 30 }}
					>
						<Col span={12}>
							<Row>
								<Col span={12}>
									<Typography.Text strong>Nama Customer</Typography.Text>
								</Col>
								<Col span={12}>
									<Typography.Text>
										<a
											href="#"
											className="trigger-popup"
											onClick={(e) => {
												e.preventDefault();

												openCustomerDetail(data.customerId);
											}}
										>
											{data.customerName ?? '-'}
										</a>
									</Typography.Text>
								</Col>
							</Row>
						</Col>
						<Col span={12}>
							<Row>
								<Col span={12}>
									<Typography.Text strong>No.MKL</Typography.Text>
								</Col>
								<Col span={12}>
									<Typography.Text>{data.mklNo ?? '-'}</Typography.Text>
								</Col>
							</Row>
						</Col>
					</Row>

					<Row
						gutter={20}
						style={{ marginBottom: 30 }}
					>
						<Col span={12}>
							<Row>
								<Col span={12}>
									<Typography.Text strong>Group Customer BMPP</Typography.Text>
								</Col>
								<Col span={12}>
									<Typography.Text>
										{data.bmppGroupId ? (
											<a
												href="#"
												className="trigger-popup"
												onClick={(e) => {
													e.preventDefault();

													openCustomerGroupDetail(data.bmppGroupId);
												}}
											>
												{data.bmppGroupName ?? '-'}
											</a>
										) : (
											<>{data.bmppGroupName ?? '-'}</>
										)}
									</Typography.Text>
								</Col>
							</Row>
						</Col>
						<Col span={12}>
							<Row>
								<Col span={12}>
									<Typography.Text strong>No.SPF</Typography.Text>
								</Col>
								<Col span={12}>
									<Typography.Text>
										{data.spfId && data.resolutionId ? (
											<a
												href="#"
												className="trigger-popup"
												onClick={(e) => {
													e.preventDefault();

													navigate('SPFPage', {
														spfId: data.spfId,
														napResolutionId: data.resolutionId,
														onBack: () =>
															navigate('MonitoringCovenantListPage'),
														viewOnly: true,
													});
												}}
											>
												{data.spfNo ?? '-'}
											</a>
										) : (
											<>{data.spfNo ?? '-'}</>
										)}
									</Typography.Text>
								</Col>
							</Row>
						</Col>
					</Row>

					<Row
						gutter={20}
						style={{ marginBottom: 30 }}
					>
						<Col span={12}>
							<Row>
								<Col span={12}>
									<Typography.Text strong>FRM</Typography.Text>
								</Col>
								<Col span={12}>
									<Typography.Text>{data.frmName ?? '-'}</Typography.Text>
								</Col>
							</Row>
						</Col>
						<Col span={12}>
							<Row>
								<Col span={12}>
									<Typography.Text strong>FBDH</Typography.Text>
								</Col>
								<Col span={12}>
									<Typography.Text>{data.fbdhName ?? '-'}</Typography.Text>
								</Col>
							</Row>
						</Col>
					</Row>

					<Row
						gutter={20}
						style={{ marginBottom: 30 }}
					>
						<Col span={12}>
							<Row>
								<Col span={12}>
									<Typography.Text strong>Sifat Limit</Typography.Text>
								</Col>
								<Col span={12}>
									<Typography.Text>
										{data.financingLimitType ?? '-'}
									</Typography.Text>
								</Col>
							</Row>
						</Col>
						<Col span={12}>
							<Row>
								<Col span={12}>
									<Typography.Text strong>Jenis Limit</Typography.Text>
								</Col>
								<Col span={12}>
									<Typography.Text>{data.plafonType ?? '-'}</Typography.Text>
								</Col>
							</Row>
						</Col>
					</Row>

					<Row
						gutter={20}
						style={{ marginBottom: 50 }}
					>
						<Col span={12}>
							<Row>
								<Col span={12}>
									<Typography.Text strong>Sumber Order</Typography.Text>
								</Col>
								<Col span={12}>
									<Typography.Text>{data.orderSource ?? '-'}</Typography.Text>
								</Col>
							</Row>
						</Col>
					</Row>
				</Fragment>
			)}

			<Divider />

			{/* Search Filters */}
			{userData?.groupName !== 'SECOM' && (
				<Fragment>
					<Form
						form={form}
						layout="inline"
					>
						<Form.Item
							label="Kata Kunci"
							name="searchBy"
						>
							<Select
								style={{ width: 200 }}
								placeholder="Pilih kriteria"
								allowClear
								onChange={handleSearchByChange}
								options={[
									{ label: 'Kategori', value: 'category' },
									{ label: 'Priority', value: 'priority' },
									{ label: 'Tanggal Upload', value: 'uploadDate' },
									{ label: 'Status', value: 'status' },
								]}
							/>
						</Form.Item>

						{searchBy && (
							<Form.Item
								label={
									searchBy === 'category'
										? 'Kategori'
										: searchBy === 'priority'
											? 'Priority'
											: searchBy === 'uploadDate'
												? 'Tanggal Upload'
												: 'Status'
								}
								name="searchValue"
							>
								{searchBy === 'uploadDate' ? (
									<div style={{ display: 'flex', gap: 8 }}>
										<DatePicker
											style={{ width: 150 }}
											format="DD/MM/YYYY"
											placeholder="Dari"
											onChange={(date) => {
												if (date) {
													const startDate = dayjs(date).format('DD/MM/YYYY');

													setDateRange((prev) =>
														prev ? [startDate, prev[1]] : [startDate, ''],
													);
													setSearchValue(
														`${startDate} - ${dateRange ? dateRange[1] : ''}`,
													);
												}
											}}
										/>
										<DatePicker
											style={{ width: 150 }}
											format="DD/MM/YYYY"
											placeholder="Sampai"
											onChange={(date) => {
												if (date) {
													const endDate = dayjs(date).format('DD/MM/YYYY');

													setDateRange((prev) =>
														prev ? [prev[0], endDate] : ['', endDate],
													);
													setSearchValue(
														`${dateRange ? dateRange[0] : ''} - ${endDate}`,
													);
												}
											}}
										/>
									</div>
								) : (
									<Select
										style={{ width: 200 }}
										placeholder={`Pilih ${
											searchBy === 'category'
												? 'kategori'
												: searchBy === 'priority'
													? 'priority'
													: 'status'
										}`}
										allowClear
										options={
											searchBy === 'category'
												? [
														{ label: 'Financial', value: 'Financial' },
														{ label: 'Non Financial', value: 'Non Financial' },
													]
												: searchBy === 'priority'
													? [
															{ label: 'High', value: 'high' },
															{ label: 'Medium', value: 'medium' },
															{ label: 'Low', value: 'low' },
														]
													: searchBy === 'status'
														? [
																{ label: 'Done', value: 'done' },
																{ label: 'Pending', value: 'pending' },
															]
														: []
										}
										onChange={(value) => {
											if (value) {
												setSearchValue(value);
											} else {
												setSearchValue('');
											}
										}}
									/>
								)}
							</Form.Item>
						)}

						<Form.Item>
							<Space>
								<Button
									type="primary"
									onClick={handleSearch}
									disabled={!searchBy}
								>
									SEARCH
								</Button>
								{searchValue && (
									<Button
										onClick={() => {
											setSearchBy('');
											setSearchValue('');
											setDateRange(null);
											form.resetFields();
										}}
									>
										RESET
									</Button>
								)}
							</Space>
						</Form.Item>
					</Form>
					<Divider />
				</Fragment>
			)}

			{/* Covenant Table */}
			{userData?.groupName !== 'SECOM' ? (
				<div style={{ marginBottom: 16 }}>
					<Typography.Title
						level={3}
						style={{ margin: 0 }}
					>
						List Tabel Monitoring Covenant
					</Typography.Title>
				</div>
			) : (
				<Fragment>
					<div style={{ marginBottom: 16 }}>
						<Typography.Title
							level={3}
							style={{ margin: 0, backgroundColor: '#E8F1FF', padding: 16 }}
						>
							Informasi Dokumen Covenant
						</Typography.Title>
					</div>
					{filteredCovenants.some((covenant) => covenant.status !== 'done') && (
						<Alert
							message={
								<span style={{ color: '#E10101', fontWeight: 'bold' }}>
									Dokumen covenant debitur Anda belum lengkap. Mohon lengkapi
									dokumen covenant
								</span>
							}
							type="info"
							className="custom-alert-info"
							showIcon
							style={{ marginBottom: 16 }}
						/>
					)}
				</Fragment>
			)}

			<Table
				style={{ marginBlock: 20 }}
				bordered
				scroll={{ x: 'max-content' }}
				dataSource={filteredCovenants.map((record, key) => ({
					...record,
					key,
				}))}
				pagination={false}
				columns={[
					{
						title: 'No',
						align: 'center',
						fixed: 'left',
						width: 60,
						render: (_, __, index) => index + 1,
					},
					{
						title: 'Edit',
						hidden:
							userData?.groupName !== 'FRM' && userData?.groupName !== 'FCOH',
						align: 'center',
						width: 80,
						render: (_, record) => {
							const isFCOH = userData?.groupName === 'FCOH';
							const modalToOpen = isFCOH
								? editCovenantFCOHModal
								: editCovenantFRMModal;

							return (
								<Button
									type="link"
									icon={<EditOutlined />}
									onClick={() => {
										modalToOpen.open({
											covenantData: record as CovenantEditData,
										});
									}}
								/>
							);
						},
					},
					{
						title: 'Covenant',
						dataIndex: 'covenant',
						render: (value) => value ?? '-',
					},
					{
						title: 'Kategori',
						render: (_, record) => {
							const categoryValue = record.category?.value;
							return categoryValue ?? '-';
						},
					},
					{
						title: 'Priority',
						render: (_, record) => {
							const priorityValue = record.priority?.value;
							if (priorityValue?.toLowerCase() === 'high') return 'High';
							if (priorityValue?.toLowerCase() === 'medium') return 'Medium';
							if (priorityValue?.toLowerCase() === 'low') return 'Low';
							return priorityValue ?? '-';
						},
					},
					{
						title: 'Repeat',
						render: (_, record) => record.repeatFrequency?.value ?? '-',
					},
					{
						title: 'Jenis Monitoring',
						render: (_, record) => record.monitoringType?.value ?? '-',
					},
					{
						title: 'Analisa Lebih Lanjut',
						render: (_, record) => record.furtherAnalysis?.value ?? '-',
					},
					{
						title: 'Tanggal Upload Dokumen',
						dataIndex: 'uploadDocumentDate',
						render: (value) => {
							if (!value) return '-';
							try {
								// Handle different possible date formats from API
								const date = new Date(value);
								if (isNaN(date.getTime())) return '-';

								// Format as DD/MM/YYYY
								const day = String(date.getDate()).padStart(2, '0');
								const month = String(date.getMonth() + 1).padStart(2, '0');
								const year = date.getFullYear();
								return `${day}/${month}/${year}`;
							} catch {
								return '-';
							}
						},
					},
					{
						title: 'Status',
						dataIndex: 'status',
						render: (value) => {
							if (value?.toLowerCase() === 'done') return 'Done';
							if (value?.toLowerCase() === 'pending') return 'Pending';
							return value ?? '-';
						},
					},
					{
						title: 'Due Date',
						dataIndex: 'dueDate',
						render: (value) => (value ? formatDate(value) : '-'),
					},
					{
						title: 'OD',
						dataIndex: 'overdueDays',
						render: (value) => `${value ?? 0} Hari`,
					},
					{
						title: 'Keterangan',
						dataIndex: 'notes',
						render: (value) => value ?? '-',
					},
					{
						title: 'Keterangan FCOH',
						render: (record) => {
							return record.fcohNotes ?? '-';
						},
					},
					{
						title: 'View Dokumen',
						align: 'center',
						fixed: 'right',
						width: 100,
						render: (_, record) => {
							const hasDocument =
								record.covenantDocument?.id && record.covenantDocument.url;

							return (
								<Button
									type="link"
									icon={<EyeOutlined />}
									disabled={!hasDocument}
									style={{
										color: hasDocument ? '#1557BC' : '#bababa',
										cursor: hasDocument ? 'pointer' : 'not-allowed',
									}}
									onClick={(e) => {
										e.preventDefault();
										if (hasDocument && record.covenantDocument?.id) {
											// Map covenantDocument to FleetFile format
											const fleetFile = {
												id: record.covenantDocument.id,
												name: record.covenantDocument.name,
												path: record.covenantDocument.path,
												format: record.covenantDocument.format as
													| 'pdf'
													| 'jpg'
													| 'jpeg'
													| 'png',
												url: record.covenantDocument.url,
											};
											openPreviewModal(fleetFile);
										}
									}}
								/>
							);
						},
					},
				]}
			/>

			{/* Edit Covenant Modals */}
			<EditCovenantFRMModal {...editCovenantFRMModal} />
			<EditCovenantFCOHModal {...editCovenantFCOHModal} />
		</Fragment>
	);
};

export default MonitoringCovenantPage;
